import React, {ComponentType, useState, useEffect} from 'react';
import {useDebouncedCallback} from 'use-debounce';

type WithDebounceProps<ValueType> = {
    debounceDelay?: number;
    onChange: (value: ValueType) => void;
    value: ValueType;
};

function withDebounce<P extends WithDebounceProps<ValueType>, ValueType>(WrappedComponent: ComponentType<P>, debounceDelay = 800) {
    return function DebouncedComponent(props: Omit<P, 'onChange' | 'value'> & WithDebounceProps<ValueType>) {
        const {onChange, value, ...restProps} = props as P & WithDebounceProps<ValueType>;

        const [localValue, setLocalValue] = useState<ValueType>(value);

        useEffect(() => {
            setLocalValue(value);
        }, [value]);

        const debouncedChangeHandler = useDebouncedCallback((newValue: ValueType) => {
            if (onChange) onChange(newValue);
        }, debounceDelay);

        const handleChange = (newValue: ValueType): void => {
            setLocalValue(newValue);
            debouncedChangeHandler(newValue);
        };

        return <WrappedComponent {...(restProps as P)} onChange={handleChange} value={localValue} />;
    };
}

export default withDebounce;
